<template>
  <div class="expense-create-container">
    <!-- Header -->
    <div class="page-header">
      <div>
        <h1>{{ isEdit ? 'Modifier la Dépense' : 'Nouvelle Dépense' }}</h1>
        <p class="subtitle">
          {{ isEdit ? expense.reference_number : 'Créer une nouvelle dépense ou revenu' }}
        </p>
      </div>
      <router-link
        to="/expenses"
        class="btn btn-secondary"
      >
        <i class="fas fa-arrow-left" /> Retour
      </router-link>
    </div>

    <form
      class="expense-form"
      @submit.prevent="saveExpense"
    >
      <!-- Main Information Card -->
      <div class="form-card">
        <h3 class="card-title">
          Informations Principales
        </h3>

        <div class="form-row">
          <div class="form-group full-width">
            <label class="required">Type de Transaction</label>
            <div class="radio-group">
              <label
                class="radio-option"
                :class="{ active: form.expense_type === 'OUT' }"
              >
                <input
                  v-model="form.expense_type"
                  type="radio"
                  value="OUT"
                  required
                >
                <div class="radio-content">
                  <div class="radio-icon-wrapper expense">
                    <i class="fas fa-arrow-up" />
                  </div>
                  <div class="radio-text">
                    <i class="fas fa-minus-circle" />
                    <span>Dépense (Sortie)</span>
                  </div>
                </div>
              </label>
              <label
                class="radio-option"
                :class="{ active: form.expense_type === 'IN' }"
              >
                <input
                  v-model="form.expense_type"
                  type="radio"
                  value="IN"
                  required
                >
                <div class="radio-content">
                  <div class="radio-icon-wrapper income">
                    <i class="fas fa-arrow-down" />
                  </div>
                  <div class="radio-text">
                    <i class="fas fa-plus-circle" />
                    <span>Revenu (Entrée)</span>
                  </div>
                </div>
              </label>
            </div>
          </div>
        </div>

        <div class="form-row">
          <!-- Date -->
          <div class="form-group">
            <label class="required">Date</label>
            <input
              v-model="form.date"
              type="date"
              required
            >
          </div>

          <!-- Category -->
          <div class="form-group">
            <label>Catégorie</label>
            <select v-model="form.expense_category">
              <option :value="null">
                Sélectionner une catégorie
              </option>
              <option
                v-for="cat in categories"
                :key="cat.id"
                :value="cat.id"
              >
                {{ cat.name }}
              </option>
            </select>
          </div>

          <!-- Vendor -->
          <div
            v-if="!isDriverSalaryCategory && !isDepotRentalCategory && !isCarRentalCategory"
            class="form-group"
          >
            <label>Fournisseur</label>
            <select v-model="form.vendor">
              <option :value="null">
                Sélectionner un fournisseur
              </option>
              <option
                v-for="vendor in vendors"
                :key="vendor.id"
                :value="vendor.id"
              >
                {{ vendor.name }}
              </option>
            </select>
          </div>

          <!-- Driver (for Salaire Drivers category) -->
          <div
            v-if="isDriverSalaryCategory"
            class="form-group"
          >
            <label class="required">Livreur</label>
            <select
              v-model="form.driver"
              required
              @change="onDriverChange"
            >
              <option :value="null">
                Sélectionner un livreur
              </option>
              <option
                v-for="driver in drivers"
                :key="driver.id"
                :value="driver.id"
              >
                {{ driver.username }} - {{ driver.payment_info ? (driver.payment_info.pay_type === 'salary' ? 'Salaire' : 'Commission') : 'N/A' }}
              </option>
            </select>
            <small
              v-if="selectedDriverInfo"
              class="form-text"
            >
              Type: {{ selectedDriverInfo.pay_type_display }} |
              Montant: {{ selectedDriverInfo.compensation ? selectedDriverInfo.compensation.toFixed(3) + ' DT' : 'N/A' }}
            </small>
          </div>

          <!-- Supplier (Internal Client) - hidden for special categories -->
          <div
            v-if="!isDriverSalaryCategory && !isDepotRentalCategory && !isCarRentalCategory"
            class="form-group"
          >
            <label>Client (Fournisseur)</label>
            <select v-model="form.supplier">
              <option :value="null">
                Sélectionner un fournisseur interne
              </option>
              <option
                v-for="sup in suppliers"
                :key="sup.id"
                :value="sup.id"
              >
                {{ sup.username }}
              </option>
            </select>
          </div>

          <!-- Depot (for Loyer Dépôts category) -->
          <div
            v-if="isDepotRentalCategory"
            class="form-group"
          >
            <label class="required">Dépôt</label>
            <select
              v-model="form.depot"
              required
              @change="onDepotChange"
            >
              <option :value="null">
                Sélectionner un dépôt
              </option>
              <option
                v-for="depot in depots"
                :key="depot.id"
                :value="depot.id"
              >
                {{ depot.name }} - {{ depot.monthly_rent_amount ? parseFloat(depot.monthly_rent_amount).toFixed(3) + ' DT/mois' : 'N/A' }}
              </option>
            </select>
            <small
              v-if="selectedDepotInfo"
              class="form-text"
            >
              Loyer mensuel: {{ selectedDepotInfo.monthly_rent_amount ? parseFloat(selectedDepotInfo.monthly_rent_amount).toFixed(3) + ' DT' : 'N/A' }}
            </small>
          </div>

          <!-- Car (for Location véhicule category) -->
          <div
            v-if="isCarRentalCategory"
            class="form-group"
          >
            <label class="required">Voiture</label>
            <select
              v-model="form.car"
              required
              @change="onCarChange"
            >
              <option :value="null">
                Sélectionner une voiture
              </option>
              <option
                v-for="car in cars"
                :key="car.id"
                :value="car.id"
              >
                {{ car.serial_number }} - {{ car.brand }} {{ car.model }} ({{ car.monthly_rental_amount ? parseFloat(car.monthly_rental_amount).toFixed(3) + ' DT/mois' : 'N/A' }})
              </option>
            </select>
            <small
              v-if="selectedCarInfo"
              class="form-text"
            >
              Loyer mensuel: {{ selectedCarInfo.monthly_rental_amount ? parseFloat(selectedCarInfo.monthly_rental_amount).toFixed(3) + ' DT' : 'N/A' }}
            </small>
          </div>
        </div>

        <div class="form-row">
          <!-- Description -->
          <div class="form-group full-width">
            <label>Description</label>
            <textarea
              v-model="form.description"
              rows="3"
              placeholder="Description de la dépense/revenu"
            />
          </div>
        </div>

        <div class="form-row">
          <!-- Notes -->
          <div class="form-group full-width">
            <label>Notes</label>
            <textarea
              v-model="form.notes"
              rows="3"
              placeholder="Notes additionnelles (optionnel)"
            />
          </div>
        </div>

        <div class="form-row">
          <!-- Attachments -->
          <div class="form-group full-width">
            <label>Pièces Jointes</label>

            <!-- Existing Attachments (when editing) -->
            <div
              v-if="isEdit && existingAttachments.length > 0"
              class="existing-attachments"
            >
              <div class="existing-attachments-header">
                <i class="fas fa-paperclip" />
                <span>Pièces jointes existantes ({{ existingAttachments.length }})</span>
              </div>
              <div class="existing-attachments-list">
                <div
                  v-for="attachment in existingAttachments"
                  :key="attachment.id"
                  class="existing-attachment-item"
                >
                  <div class="attachment-icon">
                    <i
                      class="fas"
                      :class="getFileIcon(attachment.filename)"
                    />
                  </div>
                  <div class="attachment-info">
                    <div class="attachment-name">
                      {{ attachment.filename }}
                    </div>
                    <div class="attachment-date">
                      Ajouté le {{ formatDate(attachment.created_at) }}
                    </div>
                  </div>
                  <button
                    type="button"
                    class="btn-download"
                    @click="downloadExistingAttachment(attachment)"
                  >
                    <i class="fas fa-download" />
                  </button>
                  <button
                    type="button"
                    class="btn-delete-attachment"
                    @click="deleteExistingAttachment(attachment)"
                  >
                    <i class="fas fa-trash" />
                  </button>
                </div>
              </div>
            </div>

            <!-- Upload Zone -->
            <div class="upload-zone">
              <input
                ref="fileInput"
                type="file"
                accept="image/*,.pdf"
                multiple
                class="file-input-hidden"
                @change="handleFileUpload"
              >
              <div
                class="upload-area"
                @click="$refs.fileInput.click()"
              >
                <div class="upload-icon">
                  <i class="fas fa-cloud-upload-alt" />
                </div>
                <div class="upload-text">
                  <p class="upload-title">
                    Cliquez pour ajouter des fichiers
                  </p>
                  <p class="upload-subtitle">
                    ou glissez-déposez vos fichiers ici
                  </p>
                  <p class="upload-hint">
                    <i class="fas fa-images" /> Images ou <i class="fas fa-file-pdf" /> PDF • Plusieurs fichiers autorisés
                  </p>
                </div>
              </div>
            </div>

            <!-- File Preview List -->
            <div
              v-if="form.attachments.length > 0"
              class="file-list"
            >
              <div class="file-list-header">
                <span>{{ form.attachments.length }} fichier{{ form.attachments.length > 1 ? 's' : '' }} sélectionné{{ form.attachments.length > 1 ? 's' : '' }}</span>
                <button
                  type="button"
                  class="btn-clear-all"
                  @click="clearAllFiles"
                >
                  <i class="fas fa-trash-alt" /> Tout supprimer
                </button>
              </div>
              <div
                v-for="(file, index) in form.attachments"
                :key="index"
                class="file-item"
              >
                <i
                  class="fas"
                  :class="file.type === 'application/pdf' ? 'fa-file-pdf' : 'fa-file-image'"
                />
                <span class="file-name">{{ file.name }}</span>
                <span class="file-size">{{ formatFileSize(file.size) }}</span>
                <button
                  type="button"
                  class="btn-remove-file"
                  @click="removeFile(index)"
                >
                  <i class="fas fa-times" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Amount Section -->
      <div class="form-card">
        <div class="card-header-with-toggle">
          <h3 class="card-title">
            Montant
          </h3>
          <label class="toggle-switch">
            <input
              v-model="form.manual_amount"
              type="checkbox"
              @change="toggleManualAmount"
            >
            <span class="toggle-slider" />
            <span class="toggle-label">Montant manuel</span>
          </label>
        </div>

        <!-- Manual Amount -->
        <div
          v-if="form.manual_amount"
          class="form-row"
        >
          <div class="form-group">
            <label class="required">Montant Total (DT)</label>
            <input
              v-model.number="form.amount"
              type="number"
              step="0.001"
              min="0"
              required
              placeholder="0.000"
            >
          </div>
        </div>

        <!-- Items Section -->
        <div v-else>
          <div class="items-header">
            <h4>Articles / Lignes de Dépense</h4>
            <button
              type="button"
              class="btn btn-sm btn-primary"
              @click="addItem"
            >
              <i class="fas fa-plus" /> Ajouter une ligne
            </button>
          </div>

          <div
            v-if="form.items.length > 0"
            class="items-table"
          >
            <table>
              <thead>
                <tr>
                  <th style="width: 40%">
                    Description
                  </th>
                  <th style="width: 15%">
                    Quantité
                  </th>
                  <th style="width: 20%">
                    Prix Unitaire
                  </th>
                  <th style="width: 20%">
                    Total
                  </th>
                  <th style="width: 5%; text-align: center;">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(item, index) in form.items"
                  :key="index"
                >
                  <td>
                    <input
                      v-model="item.description"
                      type="text"
                      placeholder="Description de l'article"
                      required
                    >
                  </td>
                  <td>
                    <input
                      v-model.number="item.quantity"
                      type="number"
                      step="0.001"
                      min="0.001"
                      required
                      @input="calculateItemTotal(index)"
                    >
                  </td>
                  <td>
                    <input
                      v-model.number="item.unit_price"
                      type="number"
                      step="0.001"
                      min="0"
                      required
                      @input="calculateItemTotal(index)"
                    >
                  </td>
                  <td>
                    <div class="total-display">
                      {{ formatAmount(item.total) }} DT
                    </div>
                  </td>
                  <td style="text-align: center;">
                    <button
                      type="button"
                      class="btn-icon danger"
                      title="Supprimer"
                      @click="removeItem(index)"
                    >
                      <i class="fas fa-trash" />
                    </button>
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td
                    colspan="3"
                    class="text-right"
                  >
                    <strong>Total Général:</strong>
                  </td>
                  <td colspan="2">
                    <strong>{{ formatAmount(calculatedTotal) }} DT</strong>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div
            v-else
            class="empty-items"
          >
            <i class="fas fa-inbox" />
            <p>Aucune ligne ajoutée. Cliquez sur "Ajouter une ligne" pour commencer.</p>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="form-actions">
        <button
          type="button"
          class="btn btn-secondary"
          @click="$router.push('/expenses')"
        >
          <i class="fas fa-times" /> Annuler
        </button>
        <button
          type="button"
          :disabled="saving"
          class="btn btn-primary"
          @click="saveExpense(false)"
        >
          <i
            class="fas"
            :class="saving ? 'fa-spinner fa-spin' : 'fa-save'"
          />
          {{ saving ? 'Enregistrement...' : (isEdit ? 'Mettre à jour' : 'Enregistrer') }}
        </button>
        <button
          v-if="!isEdit"
          type="button"
          :disabled="saving"
          class="btn btn-success"
          @click.prevent="saveAndConfirm"
        >
          <i class="fas fa-check" /> Enregistrer et Confirmer
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'ExpenseCreate',
  props: {
    id: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      isEdit: false,
      expense: null,
      saving: false,
      categories: [],
      vendors: [],
      drivers: [],
      suppliers: [], // List of internal suppliers
      depots: [],
      cars: [],
      selectedDriverInfo: null,
      selectedDepotInfo: null,
      selectedCarInfo: null,
      form: {
        expense_type: 'OUT',
        date: new Date().toISOString().split('T')[0],
        amount: 0,
        manual_amount: false,
        expense_category: null,
        vendor: null,
        driver: null,
        supplier: null, // Added supplier field
        depot: null,
        car: null,
        description: '',
        notes: '',
        attachments: [], // New files to upload
        items: [],
      },
      existingAttachments: [], // Attachments already saved on server
    }
  },
  computed: {
    isDriverSalaryCategory() {
      if (!this.form.expense_category || !this.categories.length) return false
      const selectedCategory = this.categories.find(cat => cat.id === this.form.expense_category)
      return selectedCategory && selectedCategory.name === 'Salaire Drivers'
    },
    isDepotRentalCategory() {
      if (!this.form.expense_category || !this.categories.length) return false
      const selectedCategory = this.categories.find(cat => cat.id === this.form.expense_category)
      // Check for variations: "Loyer Dépôts", "Loyer Depots", etc.
      return selectedCategory && (
        selectedCategory.name === 'Loyer Dépôts'
        || selectedCategory.name === 'Loyer Depots'
        || (selectedCategory.name.toLowerCase().includes('loyer') && selectedCategory.name.toLowerCase().includes('depot'))
      )
    },
    isCarRentalCategory() {
      if (!this.form.expense_category || !this.categories.length) return false
      const selectedCategory = this.categories.find(cat => cat.id === this.form.expense_category)
      // Check for variations: "Location véhicule", "Location vehicule", etc.
      return selectedCategory && (
        selectedCategory.name === 'Location véhicule'
        || selectedCategory.name === 'Location vehicule'
        || (selectedCategory.name.toLowerCase().includes('location') && selectedCategory.name.toLowerCase().includes('v') && selectedCategory.name.toLowerCase().includes('hicule'))
      )
    },
    itemsTotal() {
      return this.form.items.reduce((sum, item) => sum + (item.total || 0), 0)
    },
    calculatedTotal() {
      return this.form.manual_amount ? this.form.amount : this.itemsTotal
    },
  },
  async mounted() {
    await Promise.all([
      this.loadCategories(),
      this.loadVendors(),
      this.fetchDrivers(),
      this.fetchSuppliers(), // Fetch internal suppliers
      this.fetchDepots(),
      this.fetchCars(),
    ])

    if (this.id) {
      this.isEdit = true
      await this.loadExpense()
    } else {
      // Add one empty item by default
      this.addItem()
    }
  },
  methods: {
    async fetchDrivers() {
      try {
        const response = await axios.get('/api/expenses/drivers/')
        this.drivers = response.data
      } catch (error) {
        this.$toast.error('Erreur lors du chargement des livreurs')
      }
    },

    async fetchSuppliers() {
      try {
        const response = await axios.get('/api/auth/users/suppliers/')
        this.suppliers = response.data.results || response.data
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching suppliers:', error)
        if (this.$toast) {
          this.$toast.error('Erreur lors du chargement des fournisseurs')
        }
      }
    },

    onDriverChange() {
      if (!this.form.driver) {
        this.selectedDriverInfo = null
        return
      }

      const driver = this.drivers.find(d => d.id === this.form.driver)
      if (driver && driver.payment_info) {
        this.selectedDriverInfo = driver.payment_info

        if (driver.payment_info.pay_type === 'commission') {
          // For commission: use items table with pre-filled unit price
          const unitPrice = driver.payment_info.compensation || 0
          this.form.manual_amount = false
          this.form.items = [{
            description: 'colis',
            quantity: 1,
            unit_price: unitPrice,
            total: unitPrice * 1, // Calculate initial total
            notes: '',
          }]
        } else {
          // For salary: use manual amount
          this.form.manual_amount = true
          this.form.amount = driver.payment_info.compensation || 0
          this.form.items = []
        }
      } else {
        this.selectedDriverInfo = null
      }
    },

    async fetchDepots() {
      try {
        const response = await axios.get('/api/expenses/depots/')
        this.depots = response.data
      } catch (error) {
        this.$toast.error('Erreur lors du chargement des dépôts')
      }
    },

    async fetchCars() {
      try {
        const response = await axios.get('/api/cars/', {
          params: { is_rented: true },
        })
        this.cars = response.data.results || response.data
      } catch (error) {
        this.$toast.error('Erreur lors du chargement des voitures')
      }
    },

    onCarChange() {
      if (!this.form.car) {
        this.selectedCarInfo = null
        return
      }

      const car = this.cars.find(c => c.id === this.form.car)
      if (car && car.monthly_rental_amount) {
        this.selectedCarInfo = car
        // Enable manual amount and auto-populate with monthly rent
        this.form.manual_amount = true
        this.form.amount = parseFloat(car.monthly_rental_amount) || 0
      } else {
        this.selectedCarInfo = null
      }
    },

    onDepotChange() {
      if (!this.form.depot) {
        this.selectedDepotInfo = null
        return
      }

      const depot = this.depots.find(d => d.id === this.form.depot)
      if (depot && depot.monthly_rent_amount) {
        this.selectedDepotInfo = depot
        // Enable manual amount and auto-populate with monthly rent
        this.form.manual_amount = true
        this.form.amount = parseFloat(depot.monthly_rent_amount) || 0
      } else {
        this.selectedDepotInfo = null
      }
    },

    async loadExpense() {
      try {
        const response = await axios.get(`/api/expenses/detail/${this.id}/`)
        this.expense = response.data

        // Store existing attachments
        this.existingAttachments = response.data.attachments || []

        // Populate form - ensure manual_amount is boolean (not null)
        this.form = {
          expense_type: this.expense.expense_type,
          date: this.expense.date,
          amount: parseFloat(this.expense.amount),
          manual_amount: this.expense.manual_amount === true, // Convert null to false
          expense_category: this.expense.expense_category,
          vendor: this.expense.vendor,
          driver: this.expense.driver,
          supplier: this.expense.supplier, // Populating supplier field
          depot: this.expense.depot,
          car: this.expense.car,
          description: this.expense.description,
          notes: this.expense.notes || '',
          attachments: [], // New files to upload
          items: this.expense.items && this.expense.items.length > 0
            ? this.expense.items.map(item => ({
              id: item.id,
              description: item.description,
              quantity: parseFloat(item.quantity),
              unit_price: parseFloat(item.unit_price),
              total: parseFloat(item.total),
              notes: item.notes || '',
            }))
            : [], // Empty array if no items
        }

        // Load driver info if driver is selected
        if (this.form.driver) {
          const driver = this.drivers.find(d => d.id === this.form.driver)
          if (driver && driver.payment_info) {
            this.selectedDriverInfo = driver.payment_info
          }
        }

        // Load depot info if depot is selected
        if (this.form.depot) {
          const depot = this.depots.find(d => d.id === this.form.depot)
          if (depot) {
            this.selectedDepotInfo = depot
          }
        }

        // Load car info if car is selected
        if (this.form.car) {
          const car = this.cars.find(c => c.id === this.form.car)
          if (car) {
            this.selectedCarInfo = car
          }
        }
      } catch (error) {
        this.$toast.error('Erreur lors du chargement de la dépense')
        this.$router.push('/expenses')
      }
    },

    async loadCategories() {
      try {
        const response = await axios.get('/api/categories/')
        this.categories = response.data
      } catch (error) {
        // Silently fail
      }
    },

    async loadVendors() {
      try {
        const response = await axios.get('/api/vendors/')
        this.vendors = response.data.results || response.data
      } catch (error) {
        // Silently fail
      }
    },

    addItem() {
      this.form.items.push({
        description: '',
        quantity: 1,
        unit_price: 0,
        total: 0,
        notes: '',
      })
    },

    removeItem(index) {
      this.form.items.splice(index, 1)
    },

    calculateItemTotal(index) {
      const item = this.form.items[index]
      item.total = item.quantity * item.unit_price
    },

    toggleManualAmount() {
      if (this.form.manual_amount) {
        // Switching to manual - set amount to calculated total
        this.form.amount = this.calculatedTotal
      } else if (this.form.items.length === 0) {
        // Switching to items - add one item if none exist
        this.addItem()
      }
    },

    handleFileUpload(event) {
      const files = Array.from(event.target.files)
      this.form.attachments = [...this.form.attachments, ...files]
    },

    removeFile(index) {
      this.form.attachments.splice(index, 1)
      // Reset file input
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    },

    formatFileSize(bytes) {
      if (bytes < 1024) return `${bytes} B`
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    },

    clearAllFiles() {
      this.form.attachments = []
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    },

    getFileIcon(filename) {
      if (!filename) return 'fa-file'
      const ext = filename.split('.').pop().toLowerCase()
      const iconMap = {
        pdf: 'fa-file-pdf',
        jpg: 'fa-file-image',
        jpeg: 'fa-file-image',
        png: 'fa-file-image',
        gif: 'fa-file-image',
        webp: 'fa-file-image',
        doc: 'fa-file-word',
        docx: 'fa-file-word',
        xls: 'fa-file-excel',
        xlsx: 'fa-file-excel',
      }
      return iconMap[ext] || 'fa-file'
    },

    formatDate(dateString) {
      if (!dateString) return ''
      return new Date(dateString).toLocaleDateString('fr-FR')
    },

    async downloadExistingAttachment(attachment) {
      try {
        const response = await axios.get(
          `/api/expenses/${this.id}/attachments/${attachment.id}/download/`,
          { responseType: 'blob' },
        )

        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', attachment.filename || 'attachment')
        document.body.appendChild(link)
        link.click()

        link.remove()
        window.URL.revokeObjectURL(url)

        this.$toast.success('Fichier téléchargé avec succès')
      } catch (error) {
        this.$toast.error('Erreur lors du téléchargement du fichier')
      }
    },

    async deleteExistingAttachment(attachment) {
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Supprimer le fichier "${attachment.filename}" ?`)) {
        return
      }

      try {
        await axios.delete(`/api/expenses/${this.id}/attachments/${attachment.id}/delete/`)

        // Remove from local list
        this.existingAttachments = this.existingAttachments.filter(a => a.id !== attachment.id)

        this.$toast.success('Fichier supprimé avec succès')
      } catch (error) {
        this.$toast.error('Erreur lors de la suppression du fichier')
      }
    },

    async saveExpense(confirmAfter = false) {
      this.saving = true

      try {
        // Prepare form data
        const formData = new FormData()

        // Add basic fields
        formData.append('expense_type', this.form.expense_type)
        formData.append('date', this.form.date)
        formData.append('description', this.form.description)
        const manualAmount = this.form.manual_amount === true
        formData.append('manual_amount', manualAmount ? 'true' : 'false')

        if (manualAmount) {
          formData.append('amount', this.form.amount)
        } else {
          formData.append('amount', this.calculatedTotal)
        }

        if (this.form.expense_category) {
          formData.append('expense_category', this.form.expense_category)
        }

        if (this.form.vendor) {
          formData.append('vendor', this.form.vendor)
        }

        if (this.form.driver) {
          formData.append('driver', this.form.driver)
        }

        if (this.form.supplier) {
          formData.append('supplier', this.form.supplier)
        }

        if (this.form.depot) {
          formData.append('depot', this.form.depot)
        }

        if (this.form.car) {
          formData.append('car', this.form.car)
        }

        if (this.form.notes) {
          formData.append('notes', this.form.notes)
        }

        // Add multiple files
        this.form.attachments.forEach(file => {
          formData.append('files', file)
        })

        // Add items as JSON - Send items when NOT using manual amount
        if (!manualAmount && this.form.items.length > 0) {
          const itemsToSend = this.form.items.map(item => ({
            description: item.description,
            quantity: item.quantity,
            unit_price: item.unit_price,
            notes: item.notes || '',
          }))
          formData.append('items', JSON.stringify(itemsToSend))
        }

        let response
        if (this.isEdit) {
          const expenseId = this.id || this.$route.params.id
          response = await axios.put(`/api/expenses/update/${expenseId}/`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          })
        } else {
          response = await axios.post('/api/expenses/create/', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          })
        }

        this.$toast.success(this.isEdit ? 'Dépense mise à jour avec succès' : 'Dépense créée avec succès')

        // Confirm if requested
        if (confirmAfter && response.data.id) {
          await axios.patch(`/api/expenses/confirm/${response.data.id}/`)
          this.$toast.success('Dépense confirmée')
        }

        this.$router.push('/expenses')
      } catch (error) {
        if (error.response && error.response.data) {
          const errors = error.response.data
          const errorMessages = Object.entries(errors)
            .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
            .join('\n')
          this.$toast.error(`Erreur: ${errorMessages}`)
        } else {
          this.$toast.error('Erreur lors de l\'enregistrement')
        }
      } finally {
        this.saving = false
      }
    },

    async saveAndConfirm() {
      await this.saveExpense(true)
    },

    formatAmount(amount) {
      return parseFloat(amount || 0).toFixed(3)
    },
  },
}
</script>

<style scoped>
.expense-create-container {
  padding: 20px;
  max-width: 100%;
  margin: 0 auto;
  background: #f8f9fa;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 25px;
  background: white;
  padding: 20px 25px;
  border-radius: 8px;
}

.page-header h1 {
  font-size: 28px;
  font-weight: 600;
  color: #1a202c;
  margin: 0 0 5px 0;
}

.subtitle {
  color: #6b7280;
  font-size: 14px;
  margin: 0;
  font-weight: 400;
}

.expense-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-card {
  background: white;
  border-radius: 8px;
  padding: 30px;
  border: 1px solid #e5e7eb;
}

.card-title {
  font-size: 18px;
  font-weight: 600;
  color: #1a202c;
  margin: 0 0 20px 0;
  padding-bottom: 15px;
  border-bottom: 2px solid #f3f4f6;
  display: flex;
  align-items: center;
  gap: 10px;
}

.card-title::before {
  content: '';
  width: 4px;
  height: 20px;
  background: #1a202c;
  border-radius: 2px;
}

.card-header-with-toggle {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid #f3f4f6;
}

.card-header-with-toggle .card-title {
  margin: 0;
  padding: 0;
  border: none;
}

.card-header-with-toggle .card-title::before {
  content: '';
  width: 4px;
  height: 20px;
  background: #1a202c;
  border-radius: 2px;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
}

.form-row:last-child {
  margin-bottom: 0;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-group label {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 5px;
}

.form-group label.required::after {
  content: '*';
  color: #ef4444;
  font-size: 14px;
}

.form-group input,
.form-group select,
.form-group textarea {
  padding: 10px 14px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s;
  background: white;
}

.form-group input:hover,
.form-group select:hover,
.form-group textarea:hover {
  border-color: #9ca3af;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #6b7280;
  box-shadow: 0 0 0 3px rgba(107, 114, 128, 0.1);
}

.help-text {
  font-size: 12px;
  color: #6b7280;
  margin-top: 5px;
  font-style: italic;
}

.radio-group {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
}

.radio-option {
  position: relative;
  cursor: pointer;
}

.radio-option input[type="radio"] {
  position: absolute;
  opacity: 0;
}

.radio-content {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  transition: all 0.3s;
  background: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
}

.radio-content:hover {
  border-color: #cbd5e1;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

/* Icon wrapper - circular background */
.radio-icon-wrapper {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.3s;
}

.radio-icon-wrapper.expense {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
}

.radio-icon-wrapper.income {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
}

.radio-icon-wrapper i {
  font-size: 16px;
}

.radio-icon-wrapper.expense i {
  color: #dc2626;
}

.radio-icon-wrapper.income i {
  color: #059669;
}

/* Text section with icon */
.radio-text {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.radio-text i {
  font-size: 14px;
  color: #6b7280;
  transition: all 0.3s;
}

.radio-text span {
  font-weight: 500;
  color: #374151;
  font-size: 14px;
}

/* Active state */
.radio-option.active .radio-content {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

.radio-option.active:first-child .radio-content {
  border-color: #ef4444;
  background: linear-gradient(135deg, #ffffff 0%, #fef2f2 100%);
}

.radio-option.active:first-child .radio-icon-wrapper.expense {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  box-shadow: 0 3px 8px rgba(239, 68, 68, 0.3);
}

.radio-option.active:first-child .radio-icon-wrapper.expense i {
  color: white;
}

.radio-option.active:first-child .radio-text i {
  color: #ef4444;
}

.radio-option.active:last-child .radio-content {
  border-color: #10b981;
  background: linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%);
}

.radio-option.active:last-child .radio-icon-wrapper.income {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  box-shadow: 0 3px 8px rgba(16, 185, 129, 0.3);
}

.radio-option.active:last-child .radio-icon-wrapper.income i {
  color: white;
}

.radio-option.active:last-child .radio-text i {
  color: #10b981;
}

.toggle-switch {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
}

.toggle-switch input[type="checkbox"] {
  position: absolute;
  opacity: 0;
}

.toggle-slider {
  position: relative;
  width: 48px;
  height: 24px;
  background: #d1d5db;
  border-radius: 24px;
  transition: background 0.3s;
}

.toggle-slider::before {
  content: '';
  position: absolute;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: white;
  top: 2px;
  left: 2px;
  transition: transform 0.3s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.toggle-switch input:checked + .toggle-slider {
  background: #1a202c;
}

.toggle-switch input:checked + .toggle-slider::before {
  transform: translateX(24px);
}

.toggle-label {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}

.items-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.items-header h4 {
  font-size: 16px;
  font-weight: 600;
  color: #1a202c;
  margin: 0;
}

.items-table {
  overflow-x: auto;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.items-table table {
  width: 100%;
  border-collapse: collapse;
}

.items-table th {
  text-align: left;
  padding: 12px;
  background: #f9fafb;
  font-size: 13px;
  font-weight: 600;
  color: #4b5563;
  border-bottom: 1px solid #e5e7eb;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.items-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #f3f4f6;
  background: white;
}

.items-table tbody tr:hover td {
  background: #f9fafb;
}

.items-table input {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s;
}

.items-table input:focus {
  outline: none;
  border-color: #6b7280;
  box-shadow: 0 0 0 2px rgba(107, 114, 128, 0.1);
}

.total-display {
  font-weight: 600;
  font-family: 'Courier New', monospace;
  color: #1a202c;
  font-size: 14px;
}

.items-table tfoot td {
  padding: 15px 12px;
  font-size: 15px;
  background: #f9fafb;
  border-top: 2px solid #e5e7eb;
  font-weight: 600;
}

.text-right {
  text-align: right;
}

.empty-items {
  padding: 60px 20px;
  text-align: center;
  color: #6b7280;
  background: #f9fafb;
  border-radius: 8px;
  border: 2px dashed #d1d5db;
}

.empty-items i {
  font-size: 48px;
  color: #d1d5db;
  margin-bottom: 15px;
}

.empty-items p {
  font-size: 14px;
  margin: 0;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 0 0 0;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: #1a202c;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #374151;
  transform: translateY(-1px);
}

.btn-success {
  background: #10b981;
  color: white;
}

.btn-success:hover:not(:disabled) {
  background: #059669;
  transform: translateY(-1px);
}

.btn-secondary {
  background: #f3f4f6;
  color: #4b5563;
  border: 1px solid #d1d5db;
}

.btn-secondary:hover {
  background: #e5e7eb;
}

.btn-sm {
  padding: 8px 16px;
  font-size: 13px;
}

.btn-icon {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: #f3f4f6;
  color: #4b5563;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-icon:hover {
  background: #e5e7eb;
}

.btn-icon.danger {
  background: #fee2e2;
  color: #991b1b;
}

.btn-icon.danger:hover {
  background: #fecaca;
}

/* Upload Zone Styles */
.upload-zone {
  margin-bottom: 16px;
}

.file-input-hidden {
  display: none;
}

.upload-area {
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  padding: 40px 20px;
  text-align: center;
  background: #f9fafb;
  cursor: pointer;
  transition: all 0.3s ease;
}

.upload-area:hover {
  border-color: #6366f1;
  background: #eef2ff;
}

.upload-icon {
  margin-bottom: 16px;
}

.upload-icon i {
  font-size: 48px;
  color: #9ca3af;
  transition: all 0.3s ease;
}

.upload-area:hover .upload-icon i {
  color: #6366f1;
  transform: translateY(-4px);
}

.upload-text {
  color: #6b7280;
}

.upload-title {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 8px 0;
}

.upload-subtitle {
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 12px 0;
}

.upload-hint {
  font-size: 13px;
  color: #9ca3af;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.upload-hint i {
  font-size: 14px;
}

.upload-hint .fa-images {
  color: #3b82f6;
}

.upload-hint .fa-file-pdf {
  color: #ef4444;
}

/* Existing Attachments Styles */
.existing-attachments {
  margin-bottom: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  background: white;
}

.existing-attachments-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: #f0f9ff;
  border-bottom: 1px solid #e5e7eb;
  font-size: 14px;
  font-weight: 600;
  color: #0369a1;
}

.existing-attachments-header i {
  color: #0369a1;
}

.existing-attachments-list {
  padding: 8px;
}

.existing-attachment-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 8px;
  margin-bottom: 8px;
  transition: all 0.2s;
}

.existing-attachment-item:last-child {
  margin-bottom: 0;
}

.existing-attachment-item:hover {
  background: #f3f4f6;
}

.existing-attachment-item .attachment-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.existing-attachment-item .attachment-icon i {
  font-size: 20px;
  color: #6b7280;
}

.existing-attachment-item .attachment-icon .fa-file-pdf {
  color: #ef4444;
}

.existing-attachment-item .attachment-icon .fa-file-image {
  color: #3b82f6;
}

.existing-attachment-item .attachment-info {
  flex: 1;
  min-width: 0;
}

.existing-attachment-item .attachment-name {
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.existing-attachment-item .attachment-date {
  font-size: 12px;
  color: #9ca3af;
  margin-top: 2px;
}

.btn-download {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: #dbeafe;
  color: #2563eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-download:hover {
  background: #bfdbfe;
  transform: scale(1.05);
}

.btn-delete-attachment {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: #fee2e2;
  color: #dc2626;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-delete-attachment:hover {
  background: #fecaca;
  transform: scale(1.05);
}

/* File List Styles */
.file-list {
  margin-top: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  background: white;
}

.file-list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}

.btn-clear-all {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  font-size: 12px;
  background: #fee2e2;
  color: #dc2626;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 500;
}

.btn-clear-all:hover {
  background: #fecaca;
}

.file-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid #f3f4f6;
  transition: all 0.2s;
}

.file-item:last-child {
  border-bottom: none;
}

.file-item:hover {
  background: #f9fafb;
}

.file-item i.fas {
  font-size: 20px;
  color: #6b7280;
}

.file-item .fa-file-pdf {
  color: #ef4444;
}

.file-item .fa-file-image {
  color: #3b82f6;
}

.file-name {
  flex: 1;
  font-size: 14px;
  color: #1f2937;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-size {
  font-size: 13px;
  color: #9ca3af;
  font-weight: 400;
}

.btn-remove-file {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: #fee2e2;
  color: #dc2626;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
}

.btn-remove-file:hover {
  background: #fecaca;
  transform: scale(1.1);
}

.btn-remove-file i {
  font-size: 12px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .expense-create-container {
    padding: 15px;
  }

  .form-card {
    padding: 20px;
  }

  .page-header {
    flex-direction: column;
    gap: 15px;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .radio-group {
    grid-template-columns: 1fr;
  }

  .form-actions {
    flex-direction: column;
  }

  .btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
